/**
 * Очаги вредных организмов так, как их рассчитывает мобильное приложение.
 *
 * Порт Form4Schema.ochags и Form5Schema.ochags из МП — тот же, что на сервере
 * (backend/catalogs/mp_ochags.py). Нужен проверке карточки: форма №6 в МП
 * проверяет только выбранные очаги, которые по этому расчёту существуют, и
 * главным считает первый из них. Этот же расчёт используется экранами форм 4–6.
 *
 * resolve(value, attr, fallbackHandbookId) — атрибут записи справочника по ссылке.
 */

export const PRICHINA_TYPES = ['DISEASES', 'STEM_PESTS', 'LEAF_EATING', 'CONDITIONAL_ROTTENING', 'OTHER', 'NONE'];
const CAUSES_HANDBOOK_ID = 10;
const SPECIES_HANDBOOK_ID = 31;

/** String.intOrNil из МП: целое после trim, иначе 0. */
export const intOrNil = (value) => {
  const raw = value === null || value === undefined ? '' : String(value).trim();
  return /^[+-]?\d+$/.test(raw) ? parseInt(raw, 10) : 0;
};

/** String.doubleOrNil из МП: запятая → точка, иначе 0. */
export const doubleOrNil = (value) => {
  const raw = value === null || value === undefined ? '' : String(value).replace(/,/g, '.').trim();
  if (!raw) return 0;
  const number = Number(raw);
  return Number.isFinite(number) ? number : 0;
};

/** StandLayerInfoSchema.commonZapas: сырорастущий на выдел + сухостой + захламлённость. */
export const commonZapas = (tier, form1, form2) => {
  const area = form2.useActualData === true ? form1.s : form1.slp;
  return doubleOrNil(tier.zapassirga) * doubleOrNil(area) + doubleOrNil(tier.zapassuh) + doubleOrNil(tier.zapaszahl);
};

const tiersOf = (form2) => (Array.isArray(form2.plantationByTiers) ? form2.plantationByTiers : [])
  .filter(t => t && typeof t === 'object');
const compositionsOf = (tier) => (Array.isArray(tier.sostav) ? tier.sostav : []).filter(i => i && typeof i === 'object');
const signsOf = (item) => (Array.isArray(item.variant4?.damageSigns) ? item.variant4.damageSigns : [])
  .filter(s => s && typeof s === 'object');

const causeCode = (cause, resolve) => {
  const code = parseInt(resolve(cause, 'code', CAUSES_HANDBOOK_ID), 10);
  return Number.isNaN(code) ? null : code;
};

/** Наличие очага и его степень — ветки when из Form4Schema.ochags. */
export const form4State = (type, diseaseType, isSosnyak, dola) => {
  let hasOchag = false;
  if (type === 'CONDITIONAL_ROTTENING') hasOchag = isSosnyak ? dola > 0 : dola >= 10;
  else if (type === 'DISEASES') hasOchag = diseaseType === '40.7' ? dola > 0 : dola >= 10;
  else if (type === 'STEM_PESTS') hasOchag = dola > 10;
  else if (type === 'OTHER') hasOchag = dola >= 10;
  if (!hasOchag) return null;

  if (type === 'CONDITIONAL_ROTTENING' && isSosnyak) {
    if (dola >= 0 && dola <= 10) return 'WEAK';
    if (dola >= 10 && dola <= 30) return 'MEDIUM';
    return dola > 30 ? 'STRONG' : null;
  }
  if (type === 'DISEASES' || type === 'OTHER' || type === 'CONDITIONAL_ROTTENING') {
    if (dola >= 10 && dola <= 20) return 'WEAK';
    if (dola >= 20 && dola <= 30) return 'MEDIUM';
    return dola >= 30 ? 'STRONG' : null;
  }
  if (type === 'STEM_PESTS') {
    if (dola >= 11 && dola <= 20) return 'WEAK';
    if (dola >= 20 && dola <= 30) return 'MEDIUM';
    return dola >= 30 ? 'STRONG' : null;
  }
  return null;
};

/** Коды причин с очагом по форме №4 (Form4Schema.ochags), по возрастанию кода. */
export const form4Ochags = (form1, form2, resolve) => {
  const tiers = tiersOf(form2);
  const totalZapas = tiers.reduce((sum, tier) => sum + commonZapas(tier, form1, form2), 0);

  const weighted = new Map();
  const signsByCode = new Map();
  tiers.forEach((tier) => {
    const zapas = commonZapas(tier, form1, form2);
    compositionsOf(tier).forEach((item) => {
      const share = intOrNil(item.dolap);
      signsOf(item).forEach((sign) => {
        const code = causeCode(sign.prichina, resolve);
        const key = code === null ? -1 : code;
        const dola = (share * intOrNil(sign.specificDolaPrizn)) / 10;
        weighted.set(key, (weighted.get(key) || 0) + dola * zapas);
        if (code !== null) {
          if (!signsByCode.has(code)) signsByCode.set(code, []);
          signsByCode.get(code).push({ share, item, sign });
        }
      });
    });
  });

  return [...weighted.keys()].sort((a, b) => a - b).flatMap((code) => {
    const signs = signsByCode.get(code);
    if (!signs) return [];
    const dola = totalZapas > 0 ? weighted.get(code) / totalZapas : 0;
    const prichina = signs[0].sign.prichina;
    const type = resolve(prichina, 'type', CAUSES_HANDBOOK_ID);
    const diseaseType = resolve(prichina, 'disease_type', CAUSES_HANDBOOK_ID);
    const pineShare = signs
      .filter(({ item }) => String(resolve(item.kodporoda, 'description', SPECIES_HANDBOOK_ID) || '')
        .toUpperCase().includes('СОСНА'))
      .reduce((sum, { share }) => sum + share, 0);
    const state = form4State(type, diseaseType, pineShare >= 6, dola);
    return state === null ? [] : [{ code, dola, state, prichina }];
  });
};

export const form4OchagCodes = (form1, form2, resolve) => form4Ochags(form1, form2, resolve).map(item => item.code);

export const form5State = (crowns) => crowns < 25 ? null : crowns < 50 ? 'MEDIUM' : crowns < 75 ? 'STRONG' : 'SOLID';

/** Variant5DistributionSchema.damagedTreeCrowns. */
const damagedCrowns = (variant5 = {}) => (
  13 * intOrNil(variant5.cat25) + 38 * intOrNil(variant5.cat50)
  + 63 * intOrNil(variant5.cat75) + 88 * intOrNil(variant5.cat100)
) / 100;

/** Коды причин с очагом по форме №5 (Form5Schema.ochags): повреждение крон от 25 %. */
export const form5Ochags = (form1, form2, resolve) => {
  const layers = [];
  const causes = [];
  tiersOf(form2).forEach((tier) => {
    const compositions = compositionsOf(tier);
    const crowns = compositions.reduce((sum, item) => sum + damagedCrowns(item.variant5) * intOrNil(item.dolap), 0) / 10;
    layers.push([commonZapas(tier, form1, form2), crowns]);
    compositions.filter(item => String(item.dolap ?? '') !== '+').forEach((item) => {
      (Array.isArray(item.variant5?.prichini) ? item.variant5.prichini : []).forEach((cause) => {
        const code = causeCode(cause, resolve);
        if (code === null || !PRICHINA_TYPES.includes(resolve(cause, 'type', CAUSES_HANDBOOK_ID))) return;
        if (!causes.some(item => item.code === code)) causes.push({ code, prichina: cause });
      });
    });
  });
  const totalZapas = layers.reduce((sum, [zapas]) => sum + zapas, 0);
  const divisor = totalZapas !== 0 ? totalZapas : 1;
  const crowns = layers.reduce((sum, [zapas, layerCrowns]) => sum + zapas * layerCrowns, 0) / divisor;
  const state = form5State(crowns);
  return state === null ? [] : causes.map(cause => ({ ...cause, dola: crowns, state }));
};

export const form5OchagCodes = (form1, form2, resolve) => form5Ochags(form1, form2, resolve).map(item => item.code);
